import { useEffect, useRef, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { SectionNavRail } from './formSectionNav';
import {
    useFormSectionNav,
    useSectionScrollSpy
} from './formSectionNavContext';

/**
 * The scrollable body of a fullscreen record form: a light canvas holding a
 * centered, max-width column of {@link FormSection} boxes. On mobile the
 * horizontal padding drops to 0 so the boxes go edge-to-edge; from `sm` up the
 * column is padded, capped and centered.
 */
export function FormSectionsBody({
    children,
    maxWidth = 800
}: {
    children: ReactNode;
    maxWidth?: number;
}) {
    const nav = useFormSectionNav();
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    useSectionScrollSpy(nav, container);

    return (
        <Box
            ref={(node: HTMLDivElement | null) => {
                setContainer(node);
                nav?.setContainer(node);
            }}
            sx={{
                flex: 1,
                overflowY: 'auto',
                // Clip the rail's whitespace edge rather than let it push a
                // horizontal scrollbar on tight widths.
                overflowX: 'hidden',
                bgcolor: 'background.default',
                px: { xs: 0, sm: 3 },
                py: 3
            }}
        >
            {/* The rail is positioned relative to this column, so it floats to
                the left without shifting the boxes off-centre. */}
            <Box
                sx={{
                    position: 'relative',
                    maxWidth,
                    mx: 'auto',
                    width: '100%'
                }}
            >
                <SectionNavRail />
                <Stack spacing={{ xs: 2, sm: 3 }} sx={{ width: '100%' }}>
                    {children}
                </Stack>
            </Box>
        </Box>
    );
}

/**
 * A notice — an Alert, a hint — that sits BETWEEN the section boxes of a
 * {@link FormSectionsBody} rather than inside one. It supplies the horizontal
 * inset the full-bleed boxes don't want, so a bordered notice does not glue
 * itself to the screen edge on mobile.
 *
 * The inset is PADDING on this wrapper, not a margin on the notice itself:
 * FormSectionsBody's Stack spaces its children with margins (useFlexGap is off
 * in MUI), and its `margin: 0` reset outspecifies an `sx` margin on the child —
 * a margin here would silently do nothing at all.
 */
export function FormSectionNotice({ children }: { children: ReactNode }) {
    return <Box sx={{ px: { xs: 2, sm: 0 } }}>{children}</Box>;
}

/**
 * One "white box" section of a record form: an outlined Paper with an optional
 * heading over a stack of inputs. Full-bleed on mobile (square corners, no side
 * borders); a normal rounded card from `sm` up.
 *
 * `bare` renders the children as-is — no heading row, no inner spacing Stack —
 * for sections that manage their own layout, keeping only the Paper shell.
 *
 * `titleSlot` rides at the RIGHT END of the heading row, for the one thing a
 * section says about ITSELF rather than asks for (the Befund form marks its
 * Kenndaten with the kind of Befund). In the heading it does not look
 * answerable; at the far edge it does not crowd the heading it qualifies.
 */
export default function FormSection({
    title,
    titleVariant = 'h6',
    titleSlot,
    bare = false,
    anchorId,
    sx,
    children
}: {
    title?: ReactNode;
    titleVariant?: TypographyProps['variant'];
    /** Rendered at the right end of the heading row — a chip, a badge. Needs a `title`. */
    titleSlot?: ReactNode;
    bare?: boolean;
    // Scroll target for the section nav (see formSectionNav).
    anchorId?: string;
    /** Layout on top of the box's own look — e.g. a box that scrolls in itself. */
    sx?: SxProps<Theme>;
    children: ReactNode;
}) {
    const paperRef = useRef<HTMLDivElement | null>(null);

    // A link that names this section in its hash (`…/edit#media`, from the
    // change log) opens the form scrolled to it. Read once, on mount: after
    // that the reader scrolls, not the address.
    useEffect(() => {
        if (!anchorId || window.location.hash !== `#${anchorId}`) {
            return;
        }
        const frame = requestAnimationFrame(() =>
            paperRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        );
        return () => cancelAnimationFrame(frame);
    }, [anchorId]);

    return (
        <Paper
            ref={paperRef}
            variant="outlined"
            data-section-anchor={anchorId}
            sx={[
                {
                    p: { xs: 2, sm: 3 },
                    borderRadius: { xs: 0, sm: 1 },
                    // Edge-to-edge on mobile: keep the top/bottom rules, drop
                    // the side borders that would hug the screen edges.
                    borderLeftWidth: { xs: 0, sm: 1 },
                    borderRightWidth: { xs: 0, sm: 1 }
                },
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
        >
            {bare ? (
                children
            ) : (
                <>
                    {title !== undefined && (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                                /**
                                 * Pushes the slot to the far edge. Not via
                                 * `ml: 'auto'` on the slot — Stack's margin
                                 * reset would swallow it (see
                                 * FormSectionNotice).
                                 */
                                justifyContent: 'space-between',
                                mb: 2
                            }}
                        >
                            <Typography variant={titleVariant}>
                                {title}
                            </Typography>
                            {titleSlot}
                        </Stack>
                    )}
                    <Stack spacing={2}>{children}</Stack>
                </>
            )}
        </Paper>
    );
}
